import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SdsApiService, SdsHazardStatementDto } from '../services/sds-api.service';

@Component({
  selector: 'sds-hazard-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './sds-hazard-picker.component.html',
  styleUrls: ['./sds-hazard-picker.component.css']
})
export class SdsHazardPickerComponent implements OnInit {
  @Input({ required: true }) control!: AbstractControl;
  readonly api = inject(SdsApiService);
  readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
  readonly pageSize = 10;
  catalog: SdsHazardStatementDto[] = [];
  matches: SdsHazardStatementDto[] = [];
  query = '';
  page = 0;
  expanded = false;
  loading = false;
  failed = false;

  get selected(): SdsHazardStatementDto[] { return this.control.value || []; }
  get visible(): SdsHazardStatementDto[] { return this.matches.slice(this.page * this.pageSize, (this.page + 1) * this.pageSize); }
  get range(): { start: number; end: number; total: number } {
    return { start: this.matches.length ? this.page * this.pageSize + 1 : 0,
      end: Math.min((this.page + 1) * this.pageSize, this.matches.length), total: this.matches.length };
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.filter(false));
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.filter());
    this.load();
  }

  load(): void {
    this.loading = true;
    this.failed = false;
    this.changeDetector.markForCheck();
    this.api.getHazardStatementCatalog().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: catalog => { this.catalog = catalog; this.loading = false; this.filter(); },
      error: () => { this.catalog = []; this.matches = []; this.loading = false; this.failed = true; this.changeDetector.markForCheck(); }
    });
  }

  text(statement: SdsHazardStatementDto): string {
    return this.api.hazardStatementText(statement, this.translate.currentLang());
  }

  private normalize(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l').replace(/Ł/g, 'L').toLowerCase();
  }

  filter(resetPage = true): void {
    if (resetPage) this.page = 0;
    const tokens = this.normalize(this.query.trim()).split(/\s+/).filter(Boolean);
    const selectedIds = new Set(this.selected.map(s => s.Id));
    const compactCode = this.query.replace(/\s/g, '');
    const score = (s: SdsHazardStatementDto): number => s.Code === compactCode ? 0
      : s.Code.toLowerCase() === compactCode.toLowerCase() ? 1 : 2;
    this.matches = this.catalog.filter(s => !selectedIds.has(s.Id) && tokens.every(token =>
      this.normalize([s.Code, this.text(s), s.StatementEn, s.StatementNb || '', s.StatementNn || ''].join(' ')).includes(token)
      || this.normalize(s.Code).includes(this.normalize(compactCode))))
      .sort((a, b) => score(a) - score(b) || a.Code.localeCompare(b.Code));
    this.page = Math.min(this.page, Math.max(0, Math.ceil(this.matches.length / this.pageSize) - 1));
    this.changeDetector.markForCheck();
  }

  search(value: string): void { this.query = value; this.expanded = true; this.filter(); }
  add(statement: SdsHazardStatementDto): void {
    if (this.control.disabled || this.selected.some(s => s.Id === statement.Id) || this.selected.length >= 50) return;
    this.control.setValue([...this.selected, statement]);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.searchInput?.nativeElement.focus();
  }
  remove(statement: SdsHazardStatementDto): void {
    if (this.control.disabled) return;
    this.control.setValue(this.selected.filter(s => s.Id !== statement.Id));
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.searchInput?.nativeElement.focus();
  }
}
